import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import {assert} from 'Asserts';
import {INPUT_LINE_HEIGHT} from 'styles/StyleConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import BaseEditableField from 'ui-library/BaseEditableField';
import ErrorMessage from 'ui-library/ErrorMessage';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import NumericButtonInput from 'ui-library/inputs/NumericButtonInput';
import NumericInput from 'ui-library/inputs/NumericInput';
import {NumericInputChangeEvent} from 'util/Types';

export interface EditableNumericInputFieldProps {
  /** Starting numeric value. */
  value: number | null;
  /** Action to take when numeric value entered is changed. */
  onChange: (value: number | null) => void;
  /** Error message presented when the value entered is invalid (not a number, outside specified range, etc). */
  invalidValueErrorText: string;
  /** Callback to surface whether the value entered is invalid to the caller. */
  onChangeHasError?: (hasError: boolean) => void;
  /** Whether input should be disabled & render the already selected value view only. */
  isDisabled?: boolean;
  /** If isForCreate is true, renders the numeric input directly. By default, it is wrapped in an editable
   * field that defaults to view mode, but can enter into an edit mode to make changes. */
  isForCreate?: boolean;
  /** Whether to render the editable field full width. By default is not full width. */
  isFullWidth?: boolean;
  dataTestAttrs?: DataTestAttrs;
  /** shouldUseCompactDisplay controls displaying the view mode at a more compact height rather than with the
   * same height as the editor. */
  shouldUseCompactDisplay?: boolean;
  // Props used to specify how the number should be validated: integer only, min/max values, and whether it
  // is required or can be null
  /** shouldValidateInteger specifies whether to validate that the number entered must be an int, not a float. */
  shouldValidateInteger: boolean;
  /** If provided, min is validated as the minimum value (inclusive). */
  min?: number;
  /** If provided, max is validated as the maximum value (inclusive). */
  max?: number;
  /** If isRequired is true, null is counted as an invalid value. By default, null is allowed. */
  isRequired?: boolean;
  /** Value to use in view mode when field is empty. Defaults to "Not specified" */
  placeholder?: string;
  /* If provided, wait for confirmation from this function before entering edit mode */
  confirmBeforeStartEditing?: () => Promise<boolean>;
  /* Disable and render provided tooltip if non-null */
  disableEditsWithTooltip?: string | null;
  /** If true, renders numeric input with increment/decrement buttons. Only applicable if `shouldValidateInteger`
   * is true */
  useIntegerIncrementButtons?: boolean;
}

interface NumericInputWithValidationFieldProps extends EditableNumericInputFieldProps {
  setIsValid: (isValid: boolean) => void;
}

function NumericInputWithValidationField(props: NumericInputWithValidationFieldProps): JSX.Element {
  if (props.shouldValidateInteger && props.useIntegerIncrementButtons) {
    return (
      <NumericButtonInput
        value={props.value}
        onChangeValue={({value, isValid}) => {
          props.onChange(value);
          props.setIsValid(isValid);
        }}
        disabled={Boolean(props.isDisabled)}
        minValue={props.min}
        maxValue={props.max}
        required={props.isRequired}
      />
    );
  }
  return (
    <NumericInput
      isDisabled={Boolean(props.isDisabled)}
      value={props.value}
      validateInteger={props.shouldValidateInteger}
      onChange={(e: NumericInputChangeEvent) => {
        props.onChange(e.target.numericValue);
        props.setIsValid(e.target.isValid);
      }}
      min={props.min}
      max={props.max}
      required={props.isRequired}
    />
  );
}

/**
 * Editable field wrapper around NumericInput.
 *
 * Create mode is just a thin wrapper around NumericInput; otherwise, this leverages BaseEditableField to
 * have view & edit modes, along with the corresponding action buttons to switch between modes.
 *
 * NumericInput provides validation around min/max, integer values, etc. (configured via the props). This
 * component presents a provided error message when the value is invalid.
 */
export default function EditableNumericInputField(props: EditableNumericInputFieldProps): JSX.Element {
  const [editingValue, setEditingValue] = useState<number | null>(props.value);
  const [isBeingEdited, setIsBeingEdited] = useState<boolean>(false);
  const defaultIsValid = props.isRequired ? props.value != null : true;
  const [isValid, setIsValid] = useState<boolean>(defaultIsValid);

  const isDisabled = props.isDisabled || props.disableEditsWithTooltip != null;

  const handleChangeIsValid = (newIsValid: boolean): void => {
    setIsValid(newIsValid);
    props.onChangeHasError?.(!newIsValid);
  };

  const errorMessage = !isValid ? (
    <ErrorMessage className="u-small-padding-top" error={props.invalidValueErrorText} />
  ) : null;

  useEffect(() => {
    setEditingValue(props.value);
  }, [props.value]);

  if (props.isForCreate) {
    return (
      <Tooltip text={props.disableEditsWithTooltip} isDisabled={!props.disableEditsWithTooltip}>
        <div>
          <NumericInputWithValidationField
            {...props}
            isDisabled={isDisabled}
            setIsValid={handleChangeIsValid}
          />
          {errorMessage}
        </div>
      </Tooltip>
    );
  }

  const handleCancel = (): void => {
    setIsBeingEdited(false);
    handleChangeIsValid(defaultIsValid);
    setEditingValue(props.value);
  };

  const handleComplete = (): void => {
    props.onChange(editingValue);
    setEditingValue(props.value);
    setIsBeingEdited(false);
  };

  const handleEdit = (): void => {
    setIsBeingEdited(true);
  };

  async function handleEditWithConfirmation(): Promise<void> {
    assert(props.confirmBeforeStartEditing != null, 'Expected confirmBeforeStartEditing to be provided');
    const shouldProceed = await props.confirmBeforeStartEditing();
    if (shouldProceed) {
      setIsBeingEdited(true);
    }
  }

  const editableField = (
    <div>
      <BaseEditableField
        editor={
          <NumericInputWithValidationField
            {...props}
            isDisabled={isDisabled}
            onChange={setEditingValue}
            value={editingValue}
            setIsValid={handleChangeIsValid}
          />
        }
        isBeingEdited={isBeingEdited}
        isWritable={!isDisabled}
        viewer={
          <div
            className={classNames({
              'u-faded': props.value == null,
              [css(styles.viewer)]: !props.shouldUseCompactDisplay,
            })}
          >
            {props.value ?? props.placeholder ?? 'Not specified'}
          </div>
        }
        onCancel={handleCancel}
        onComplete={handleComplete}
        onEdit={props.confirmBeforeStartEditing != null ? handleEditWithConfirmation : handleEdit}
        isCompleteEnabled={isValid}
        dataTestAttrs={props.dataTestAttrs}
        isFullWidth={props.isFullWidth}
      />
      {errorMessage}
    </div>
  );
  return (
    <Tooltip text={props.disableEditsWithTooltip} isDisabled={!props.disableEditsWithTooltip}>
      {editableField}
    </Tooltip>
  );
}

const styles = StyleSheet.create({
  viewer: {
    ...INPUT_LINE_HEIGHT,
  },
});
